import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { user } from "../../../redux/usersSlice";
import { apiGoogleLogin, login } from "../../../services/apiAuth";
import { loginSchema } from "../../../schemas/loginSchema";
import { subscriptionManager } from "../../../utils/subscriptionManager";
import {
  buildLoginFormData,
  decodeAuthQueryData,
  getAuthErrorMessage,
  getLoginRedirectUrl,
  isEmailLoginIdentifier,
  persistDecodedAuthData,
  persistUserAuthSession,
} from "../utils/authUtils";

export function useLoginFlow({ onLogin }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [apiError, setApiError] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = React.useState(false);
  const queryParamsData = searchParams.get("data");
  const errorFromQueryParams = searchParams.get("error");

  const methods = useForm({
    defaultValues: {},
    resolver: yupResolver(loginSchema),
  });

  const redirectUrl = React.useMemo(
    () => getLoginRedirectUrl(searchParams),
    [searchParams]
  );

  React.useEffect(() => {
    if (!errorFromQueryParams) {
      return;
    }

    window.alert(errorFromQueryParams);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("error");
    setSearchParams(nextSearchParams);
  }, [errorFromQueryParams, searchParams, setSearchParams]);

  React.useEffect(() => {
    const unsubscribe = subscriptionManager.subscribe((isExpired) => {
      setShowSubscriptionPopup(Boolean(isExpired));
    });

    return unsubscribe;
  }, []);

  React.useEffect(() => {
    const decodedData = decodeAuthQueryData(queryParamsData);

    if (!decodedData) {
      return;
    }

    persistDecodedAuthData(decodedData);
    navigate(redirectUrl);
  }, [navigate, queryParamsData, redirectUrl]);

  const handleLoginSuccess = React.useCallback(
    (responseData, isEmailLogin) => {
      if (isEmailLogin) {
        onLogin(
          {
            ...responseData,
            lastLogin: responseData?.user_data?.last_seen,
          },
          redirectUrl
        );
        return;
      }

      dispatch(user(responseData.user_data));
      persistUserAuthSession({
        token: responseData.token,
        userData: responseData.user_data,
      });
      navigate(redirectUrl);
    },
    [dispatch, navigate, onLogin, redirectUrl]
  );

  const submit = React.useCallback(
    (data) => {
      const isEmailLogin = isEmailLoginIdentifier(data.email);

      setApiError("");
      setLoading(true);

      login(buildLoginFormData(data), isEmailLogin)
        .then((res) => {
          handleLoginSuccess(res.data.data.responseData[0], isEmailLogin);
        })
        .catch((error) => {
          if (error?.response?.status === 423) {
            subscriptionManager.notifyExpired();
            return;
          }

          setApiError(getAuthErrorMessage(error));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [handleLoginSuccess]
  );

  const handleGoogleLogin = React.useCallback(() => {
    apiGoogleLogin()
      .then((res) => {
        window.location.href = res?.data?.auth_url;
      })
      .catch((error) => {
        if (error?.response?.status === 423) {
          subscriptionManager.notifyExpired();
        }
      });
  }, []);

  const handleForgotPassword = React.useCallback(() => {
    navigate("/forgotpassword");
  }, [navigate]);

  const handleClosePopup = React.useCallback(() => {
    setShowSubscriptionPopup(false);
  }, []);

  const handleFormError = React.useCallback((errors) => {
    console.log(errors);
  }, []);

  return {
    methods,
    apiError,
    loading,
    showSubscriptionPopup,
    submit,
    handleGoogleLogin,
    handleForgotPassword,
    handleClosePopup,
    handleFormError,
  };
}
